"use client";

import { ChevronDown, Minus, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface TravelerSelectProps {
  value?: number;
  onChange: (value: number) => void;
  label: string;
  placeholder?: string;
  invalid?: boolean;
  popoverModal?: boolean;
  min?: number;
  max?: number;
  disabled?: boolean;
}

export function TravelerSelect({
  value,
  onChange,
  label,
  placeholder = "Select travelers",
  invalid = false,
  popoverModal = false,
  min = 1,
  max = 20,
  disabled = false,
}: TravelerSelectProps) {
  const [open, setOpen] = useState(false);
  const lowerBound = Math.min(min, max);
  const upperBound = Math.max(min, max);

  const count = useMemo(() => {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) {
      return undefined;
    }
    return Math.min(upperBound, Math.max(lowerBound, n));
  }, [value, lowerBound, upperBound]);

  const currentCount = count ?? lowerBound;

  const quickSelectOptions = useMemo(() => {
    if (upperBound < lowerBound) {
      return [];
    }

    return Array.from(
      { length: upperBound - lowerBound + 1 },
      (_, index) => lowerBound + index,
    );
  }, [lowerBound, upperBound]);

  const dec = () => {
    const next = Math.max(lowerBound, currentCount - 1);
    onChange(next);
  };

  const inc = () => {
    const next = Math.min(upperBound, currentCount + 1);
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-invalid={invalid}
          disabled={disabled}
          className={cn(
            "h-14 w-full justify-between rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
            count === undefined && "text-muted-foreground",
            invalid && "border-destructive",
          )}
        >
          {count !== undefined ? (
            <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
              <span className="text-[1.5rem] font-bold leading-none tracking-tight text-foreground sm:text-[1.75rem]">
                {count}
              </span>
              <span className="h-9 w-px bg-border" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground/85">
                  Traveler{count === 1 ? "" : "s"}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  Select traveler count
                </span>
              </span>
            </span>
          ) : (
            <span className="min-w-0 text-left">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {label}
              </span>
              <span className="block truncate text-sm text-muted-foreground">
                {placeholder}
              </span>
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[320px] p-3" align="start">
        <div className="flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={dec}
            disabled={disabled || currentCount <= lowerBound}
            aria-label="Decrease travelers"
          >
            <Minus className="h-4 w-4" />
          </Button>

          <div className="min-w-0 text-center">
            <span className="text-2xl font-semibold leading-none">
              {currentCount}
            </span>
            <p className="text-xs text-muted-foreground">
              Traveler{currentCount === 1 ? "" : "s"}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={inc}
            disabled={disabled || currentCount >= upperBound}
            aria-label="Increase travelers"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-5 gap-2">
          {quickSelectOptions.map((n) => (
            <Button
              key={n}
              type="button"
              variant={currentCount === n ? "default" : "secondary"}
              onClick={() => {
                onChange(n);
                setOpen(false);
              }}
              disabled={disabled}
              className="h-8"
            >
              {n}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
